
import Vue from "vue";
import moment from "moment";
import { getDatesFromStartEnd } from "@/utils/settlementUtils";
import { db } from "@/utils/dexie";
import { getCurrentPaymentAmount } from "@/utils/orderPaymentSummary";
import {
  matchesOrderPathFilter,
  OrderPathFilter,
  resolveOrderPath,
} from "@/utils/orderPathFilter";
import MonthControl from "./MonthControl.vue";
import { colorPaletteMap } from "@/utils/colorMap";
import eventBus from "@/eventBus";
import { chartMixin } from "@/mixins/chartMixin";

import { Chart as ChartJS, registerables, ChartConfiguration } from "chart.js";
ChartJS.register(...registerables);

const colorSteps = [
  "base",
  "lighten-4",
  "lighten-3",
  "lighten-2",
  "lighten-1",
  "darken-1",
  "darken-2",
];
let colorIndexMap: Record<string, number> = {};

function getColorGroup(label: string): string {
  if (label.includes("네이버")) return "lightGreen";
  if (label.includes("스마트스토어")) return "green";
  if (
    label.includes("모바일웹") ||
    label.includes("PC쇼핑몰") ||
    label.includes("PC 쇼핑몰") ||
    label.includes("카페24")
  )
    return "blue";
  return "grey";
}

function getNextColor(label: string): string {
  const group = getColorGroup(label);
  const colors = colorPaletteMap[group] || colorPaletteMap["default"];
  const index = colorIndexMap[group] ?? 0;
  const step = colorSteps[index % colorSteps.length];
  colorIndexMap[group] = (index + 1) % colorSteps.length;
  return colors[step] ?? "#CCCCCC";
}

export default Vue.extend({
  mixins: [chartMixin],
  components: {
    MonthControl,
  },
  props: {
    calendarValue: Object as () => moment.Moment,
    orderPathFilter: {
      type: Object as () => OrderPathFilter,
      default: undefined,
    },
  },
  data: () => ({
    chart: null as ChartJS | null,
    isRendering: false,
    hasPendingRender: false,
  }),
  watch: {
    calendarValue: {
      handler() {
        this.$nextTick(() => {
          this.render();
        });
      },
      immediate: false,
    },
    "$store.state.mall_info": {
      handler() {
        this.$nextTick(() => {
          this.render();
        });
      },
      deep: true,
    },
    orderPathFilter: {
      handler() {
        this.$nextTick(() => this.render());
      },
      deep: true,
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.render();
    });
  },

  methods: {
    destroyChart() {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
    },
    createOrUpdateChart(ctx: any, config: any) {
      this.destroyChart();
      this.chart = new ChartJS(ctx, config as any) as any;
    },
    async render() {
      if (this.isRendering) {
        this.hasPendingRender = true;
        return;
      }
      this.isRendering = true;

      try {
        const canvasEl = this.$refs.chart as HTMLCanvasElement | undefined;
        if (!canvasEl) return;

        const ctx = canvasEl.getContext("2d");
        if (!ctx) return;

        const shop = this.$store.state.mall_info;
        if (!shop || !shop.mall_id || shop.shop_no === undefined) {
          return;
        }

        const start_date = this.calendarValue
          .clone()
          .startOf("month")
          .format("YYYY-MM-DD");
        const end_date = this.calendarValue
          .clone()
          .endOf("month")
          .format("YYYY-MM-DD");
        const dates = getDatesFromStartEnd(start_date, end_date);

        let dbItems = await db.results
          .where("[mall_id+shop_no]")
          .equals([shop.mall_id, String(shop.shop_no)])
          .toArray();

        const hasNoItems = dbItems.length === 0;
        const validShopNo = shop.shop_no !== undefined;
        if (hasNoItems && validShopNo && !isNaN(Number(shop.shop_no))) {
          const numItems = await db.results
            .where("[mall_id+shop_no]")
            .equals([shop.mall_id, Number(shop.shop_no)])
            .toArray();
          if (numItems.length > 0) {
            dbItems = numItems;
          }
        }

        colorIndexMap = {};

        const paymentMethodSet = new Set<string>();
        const orderDataMap: Record<string, Record<string, number>> = {};
        const refundDataMap: Record<string, Record<string, number>> = {};

        for (const item of dbItems) {
          if (!item || item.date < start_date || item.date > end_date) continue;
          const date = item.date;

          if (!orderDataMap[date]) orderDataMap[date] = {};
          if (!refundDataMap[date]) refundDataMap[date] = {};

          if (Array.isArray(item.orders)) {
            for (const order of item.orders) {
              if (!matchesOrderPathFilter(order, this.orderPathFilter))
                continue;
              const place = resolveOrderPath(
                order,
                this.orderPathFilter?.presentationMode
              ).label;
              const method =
                order.payment_method_name ||
                order.order?.payment_method_name ||
                "기타";
              const key = `${place} (${method})`;
              paymentMethodSet.add(key);

              const price = getCurrentPaymentAmount(order);
              orderDataMap[date][key] = (orderDataMap[date][key] || 0) + price;
            }
          }

          if (Array.isArray(item.refunds)) {
            for (const refund of item.refunds) {
              if (!matchesOrderPathFilter(refund, this.orderPathFilter))
                continue;
              const place = resolveOrderPath(
                refund,
                this.orderPathFilter?.presentationMode
              ).label;
              const method =
                refund.payment_method_name ||
                refund.order?.payment_method_name ||
                "기타";
              const key = `${place} (${method})`;
              paymentMethodSet.add(key);

              const amount = parseInt(refund.actual_refund_amount, 10) || 0;
              refundDataMap[date][key] =
                (refundDataMap[date][key] || 0) + amount;
            }
          }
        }

        const methodList = Array.from(paymentMethodSet);

        const orderDatasets = methodList.map((method) => {
          const color = getNextColor(method);
          return {
            label: `${method} [현재 결제]`,
            stack: "현재 결제",
            data: dates.map((d) => orderDataMap[d]?.[method] || 0),
            backgroundColor: color,
            borderColor: color,
          };
        });

        const refundDatasets = methodList.map((method) => {
          const color = getNextColor(method);
          return {
            label: `${method} [환불]`,
            stack: "환불",
            data: dates.map((d) => -(refundDataMap[d]?.[method] || 0)),
            backgroundColor: color,
            borderColor: color,
          };
        });

        const labels = dates.map((l) => moment(l).format("D"));

        const dsHairline =
          getComputedStyle(document.documentElement)
            .getPropertyValue("--ds-hairline")
            .trim() || "#ebebeb";

        const chartConfig: ChartConfiguration = {
          type: "bar",
          data: {
            labels,
            datasets: [...orderDatasets, ...refundDatasets],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
              mode: "nearest",
              intersect: false,
            },
            onClick: (event, elements) => {
              if (elements.length > 0) {
                const index = elements[0].index;
                const selectedDate = dates[index];
                eventBus.$emit("query", selectedDate, selectedDate);
              }
            },
            scales: {
              x: {
                stacked: true,
                grid: {
                  color: dsHairline,
                },
              },
              y: {
                stacked: true,
                grid: {
                  color: dsHairline,
                },
                ticks: {
                  callback: (value) =>
                    typeof value === "number" ? value.toLocaleString() : value,
                },
              },
            },
            plugins: {
              legend: {
                display: true,
                position: "left",
                align: "start",
                labels: {
                  boxWidth: 12,
                  boxHeight: 12,
                  font: {
                    family: "Geist, Inter, sans-serif",
                    size: 11,
                  },
                },
              },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    const label = context.dataset.label || "";
                    const value = context.parsed.y;
                    return value !== null
                      ? `${label}: ${value.toLocaleString()}원`
                      : label;
                  },
                },
              },
            },
          },
        };

        const existingChart = ChartJS.getChart(canvasEl);
        if (existingChart) {
          existingChart.destroy();
        }

        this.createOrUpdateChart(canvasEl, chartConfig);
      } catch (e) {
        console.error("DailyAmountCountPaymentChart render error:", e);
      } finally {
        this.isRendering = false;
        if (this.hasPendingRender) {
          this.hasPendingRender = false;
          this.$nextTick(() => {
            this.render();
          });
        }
      }
    },
  },
});
